import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import NotificationOptIn from "@/components/NotificationOptIn";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Save, Trash2, User, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";

function initials(name) {
  return (name || "?").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(user);
  const [dateNaissance, setDateNaissance] = useState("");
  const [phone, setPhone] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      setDateNaissance(data.date_naissance || "");
      setPhone(data.phone || "");
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadMe(); }, []);

  useEffect(() => {
    let revoke; let cancelled = false;
    async function fetchPhoto() {
      setPhotoUrl(null);
      if (!me?.id) return;
      try {
        const res = await api.get(`/users/${me.id}/photo`, { responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        revoke = url; setPhotoUrl(url);
      } catch (err) {
        if (err?.response?.status !== 404) {
          console.warn("Profile.fetchPhoto failed", err);
        }
      }
    }
    fetchPhoto();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [me?.id, reloadKey]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(f.type)) { toast.error("Format invalide (PNG/JPG/WEBP)"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo mise à jour");
      setReloadKey((k) => k + 1);
      refresh?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = async () => {
    if (!window.confirm("Supprimer votre photo de profil ?")) return;
    try {
      await api.delete("/me/photo");
      toast.success("Photo supprimée");
      setReloadKey((k) => k + 1);
      refresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveInfo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me", {
        date_naissance: dateNaissance || null,
        phone: phone.trim() || null,
      });
      toast.success("Profil mis à jour");
      loadMe();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!me) return null;

  return (
    <div>
      <PageHeader
        title="Mon profil"
        subtitle="Mettez à jour votre photo et votre date de naissance. Ces informations aideront vos collègues à mieux vous reconnaître."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-6 border-slate-200 lg:col-span-1" data-testid="profile-photo-card">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Photo de profil</h3>
          <div className="flex flex-col items-center">
            <div className="relative">
              {photoUrl ? (
                <img src={photoUrl} alt="moi" className="h-40 w-40 rounded-full object-cover ring-4 ring-white shadow-md" />
              ) : (
                <div className="h-40 w-40 rounded-full bg-[#0066FF]/10 text-[#0066FF] flex items-center justify-center font-heading font-bold text-4xl ring-4 ring-white shadow-md">
                  {initials(me.full_name)}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="profile-photo-upload"
                className="absolute bottom-1 right-1 h-11 w-11 rounded-full bg-[#0066FF] text-white flex items-center justify-center border-4 border-white shadow-md hover:bg-[#0052CC] transition-colors"
                disabled={uploading}
              >
                <Camera className="h-5 w-5" />
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onFile}
            />
            <p className="mt-4 text-xs text-slate-500 text-center max-w-[220px]">
              Cliquez sur l'appareil photo pour changer votre image. PNG, JPG ou WEBP, 5 Mo maximum.
            </p>
            {photoUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={removePhoto}
                data-testid="profile-photo-remove"
                className="mt-3 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer
              </Button>
            )}
          </div>
        </Card>

        <Card className="p-6 border-slate-200 lg:col-span-2" data-testid="profile-info-card">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-4 w-4 text-[#0066FF]" />
            <h3 className="font-heading font-semibold text-slate-900">Mes informations</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
            <Field label="Nom complet" value={me.full_name} />
            <Field label="Adresse e-mail" value={me.email} />
            <Field label="Poste" value={me.poste || "—"} />
            <Field label="Date d'embauche" value={me.date_embauche || "—"} />
          </div>
          <form onSubmit={saveInfo} className="border-t border-slate-100 pt-4 space-y-4">
            <div>
              <Label htmlFor="dob">Date de naissance</Label>
              <p className="text-xs text-slate-500 mb-2">Seuls le jour et le mois seront visibles par vos collègues dans l'onglet Équipe.</p>
              <Input
                id="dob"
                data-testid="profile-birthday-input"
                type="date"
                value={dateNaissance}
                onChange={(e) => setDateNaissance(e.target.value)}
                className="max-w-xs"
              />
            </div>
            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <p className="text-xs text-slate-500 mb-2">Utilisé par vos collègues sur l'onglet Équipe pour vous appeler, envoyer un SMS ou vous joindre par WhatsApp.</p>
              <Input
                id="phone"
                data-testid="profile-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+33 6 12 34 56 78"
                className="max-w-xs"
              />
            </div>
            <Button data-testid="profile-save" type="submit" disabled={saving} className="bg-[#0066FF] hover:bg-[#0052CC]">
              <Save className="h-4 w-4 mr-1" />{saving ? "…" : "Enregistrer"}
            </Button>
          </form>
        </Card>
      </div>

      <Card className="mt-5 p-6 border-slate-200" data-testid="profile-notif-card">
        <h3 className="font-heading font-semibold text-slate-900 mb-2">Notifications push</h3>
        <p className="text-sm text-slate-500 mb-4">
          Recevez une alerte instantanée sur ce téléphone dès qu'un congé est validé, un bulletin déposé ou une attestation approuvée (RH : à la moindre nouvelle demande).
          Vous pouvez activer les notifications sur chaque appareil que vous utilisez.
        </p>
        <NotificationOptIn />
      </Card>

      <Card className="mt-6 p-6 border-slate-200" data-testid="password-card">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-4 w-4 text-[#0066FF]" />
          <h3 className="font-heading font-semibold text-slate-900">Changer mon mot de passe</h3>
        </div>
        <PasswordChangeForm />
      </Card>
    </div>
  );
}

function PasswordChangeForm() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (form.next !== form.confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    if (form.next === form.current) { toast.error("Le nouveau mot de passe doit être différent de l'actuel"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Mot de passe modifié");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <Label htmlFor="pw-current">Mot de passe actuel</Label>
        <Input
          id="pw-current" data-testid="pw-current-input"
          type="password" required autoComplete="current-password"
          value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="pw-next">Nouveau mot de passe</Label>
        <Input
          id="pw-next" data-testid="pw-next-input"
          type="password" required minLength={8} autoComplete="new-password"
          value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="pw-confirm">Confirmer</Label>
        <Input
          id="pw-confirm" data-testid="pw-confirm-input"
          type="password" required minLength={8} autoComplete="new-password"
          value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
        />
      </div>
      <div className="md:col-span-3 flex items-center justify-between border-t border-slate-100 pt-4 mt-2">
        <p className="text-xs text-slate-500 flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Minimum 8 caractères. Vous restez connecté après modification.</p>
        <Button data-testid="pw-submit" type="submit" disabled={busy} className="bg-[#0066FF] hover:bg-[#0052CC]">
          <Save className="h-4 w-4 mr-1" />{busy ? "…" : "Modifier"}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-slate-900">{value}</div>
    </div>
  );
}
